import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

import { AlertCircle, CheckCircle } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { WidgetGrid } from '@/components/glitch-ui/widget-grid';
import { CreativeThumb } from '../creative-thumb';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import {
  metaCampaignOverviewQueryOptions,
  metaCreativePerformanceQueryOptions,
  metaPlacementQueryOptions,
  metaTimeSeriesQueryOptions,
  metaIntegrationStatusQueryOptions,
} from '@/lib/query-options';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { toDisplayAmount, formatMoney } from '@/lib/performance/money';
import { resolveDateWindow } from '@/lib/performance/date-window';
import { PlatformLogo } from '@/lib/performance/platform';
import { computeMetaAlerts } from '@/lib/performance/meta-alerts';
import { CHART_PURPLE, EmptyChart } from '../cells';
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCustomRangeAtom,
} from '@/store';

export const META_OVERVIEW_LAYOUT_KEY = 'meta-overview';

/** Single-platform widgets carry their platform's logo top-left (ruling 09-08). */
function MetaWidgetHeading({ title }: { title: string }) {
  return (
    <div className="mb-3 flex items-center gap-1.5">
      <PlatformLogo platform="meta" className="h-3.5 w-3.5 shrink-0" />
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
    </div>
  );
}

const EMPTY_LINE = 'No Meta data in the synced window.';

/**
 * The Meta-specific Overview widgets (ruling 09-08): the Meta-only scope
 * previously collapsed to chart + KPIs because every detail widget was
 * Google-fed. All five reuse queries that already power other tabs — no new
 * backend surface: campaign snapshot (top campaigns, lead mix), creative
 * performance (leaderboard), placements (mix), daily insights (CPL trend).
 */
export function MetaOverviewDetail({
  getTabLayout,
  onReorder,
  onResize,
  onResetTab,
}: {
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
  onResetTab?: (tabId: string) => void;
}) {
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const window = useMemo(() => resolveDateWindow(selectedDays, customRange), [selectedDays, customRange]);

  const { data: metaStatus } = useQuery(metaIntegrationStatusQueryOptions());
  const metaConnected = !!metaStatus?.is_connected;
  // Meta money renders in the Meta ad account's currency (#6278/#6182).
  const { symbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  const cs = symbol ?? '';

  const { data: campaignsResp } = useQuery(metaCampaignOverviewQueryOptions(metaConnected));
  const { data: creativeResp } = useQuery(metaCreativePerformanceQueryOptions(metaConnected));
  const { data: placementResp } = useQuery(metaPlacementQueryOptions(metaConnected, window.start_date, window.end_date));
  const { data: seriesResp } = useQuery(metaTimeSeriesQueryOptions(window.start_date, window.end_date, metaConnected));

  const campaignRows = campaignsResp?.rows ?? [];
  const money = (cents: number) => formatMoney(toDisplayAmount(cents, 'meta'), cs);

  // Cost per lead per day; days without leads carry null so the line gaps
  // honestly instead of claiming a €0 lead.
  const cplSeries = useMemo(
    () =>
      (seriesResp?.rows ?? []).map((r) => ({
        date: new Date(`${r.day}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        cpl: r.leads > 0 ? toDisplayAmount(r.spend_cents, 'meta') / r.leads : null,
      })),
    [seriesResp],
  );
  const cplHasData = cplSeries.some((p) => p.cpl !== null);

  return (
    <WidgetGrid
      layout={getTabLayout(META_OVERVIEW_LAYOUT_KEY)}
      onReorder={(l) => onReorder(META_OVERVIEW_LAYOUT_KEY, l)}
      onResize={(id, w) => onResize(META_OVERVIEW_LAYOUT_KEY, id, w)}
      onResetLayout={onResetTab ? () => onResetTab(META_OVERVIEW_LAYOUT_KEY) : undefined}
    >
      {(widgetId) => {
        switch (widgetId) {
          case 'meta-alerts': {
            const alerts = computeMetaAlerts(campaignRows, cs);
            return (
              <Card className="p-4 border border-gray-200 shadow-sm">
                <MetaWidgetHeading title="Alerts" />
                {alerts.length > 0 ? (
                  <div className="space-y-2">
                    {alerts.map((alert, i) => (
                      <div
                        key={i}
                        className={`flex items-start gap-3 rounded-lg border-l-4 px-4 py-3 ${
                          alert.severity === 'critical'
                            ? 'border-l-red-400 bg-red-50 text-red-700'
                            : 'border-l-amber-400 bg-amber-50 text-amber-700'
                        }`}
                      >
                        <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{alert.message}</p>
                          {alert.campaign && (
                            <p className={`mt-0.5 text-xs ${alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`}>{alert.campaign}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // The card empty idiom shared with the Google grid's
                  // widgets: centred, calm, not a banner (09-08).
                  <EmptyChart icon={CheckCircle} message="No issues detected. Alerts appear here when a campaign needs attention." />
                )}
              </Card>
            );
          }

          case 'meta-cpl-trend':
            return (
              <Card className="p-4 border border-gray-200 shadow-sm">
                <MetaWidgetHeading title="Cost per Lead" />
                {cplHasData ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={cplSeries} margin={{ top: 4, right: 18, bottom: 0, left: 4 }}>
                      <CartesianGrid vertical={false} stroke="#f3f4f6" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${cs}${v.toFixed(0)}`} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                        formatter={(value) => [`${cs}${Number(value).toFixed(2)}`, 'Cost per lead']}
                      />
                      <Line type="monotone" dataKey="cpl" stroke="#d946ef" strokeWidth={2.5} dot={false} connectNulls={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-[220px] items-center justify-center">
                    <p className="text-sm text-gray-500">No leads in this period.</p>
                  </div>
                )}
              </Card>
            );

          case 'meta-top-campaigns': {
            const top = [...campaignRows].sort((a, b) => b.leads - a.leads || b.spend_cents - a.spend_cents).slice(0, 5);
            return (
              <Card className="p-4 border border-gray-200 shadow-sm">
                <MetaWidgetHeading title="Top Campaigns" />
                {top.length === 0 ? (
                  <p className="py-2 text-sm text-gray-500">{EMPTY_LINE}</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {top.map((row) => (
                      <div key={row.meta_campaign_id} className="flex items-center justify-between gap-3 py-2">
                        <span className="min-w-0 truncate text-xs font-medium text-gray-900">{row.name || row.meta_campaign_id}</span>
                        <span className="whitespace-nowrap text-xs text-gray-500">
                          {row.leads.toLocaleString()} leads · {money(row.spend_cents)}
                          {row.leads > 0 && <span className="text-gray-400"> · {money(row.spend_cents / row.leads)}/lead</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          }

          case 'meta-lead-mix': {
            const withLeads = campaignRows.filter((r) => r.leads > 0).sort((a, b) => b.leads - a.leads).slice(0, 6);
            const totalLeads = withLeads.reduce((s, r) => s + r.leads, 0);
            return (
              <Card className="p-4 border border-gray-200 shadow-sm">
                <MetaWidgetHeading title="Lead Mix" />
                {withLeads.length === 0 ? (
                  // Same silhouette as the populated state: a grey ring
                  // holding its place with the note in the legend column.
                  <div className="flex items-center">
                    <ResponsiveContainer width="40%" height={160}>
                      <PieChart>
                        <Pie data={[{ value: 1 }]} cx="50%" cy="50%" innerRadius={38} outerRadius={62} dataKey="value" strokeWidth={0} isAnimationActive={false}>
                          <Cell fill="#F3F4F6" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <p className="flex-1 text-xs text-gray-500">Lead mix appears here once campaigns record leads.</p>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <ResponsiveContainer width="40%" height={160}>
                      <PieChart>
                        <Pie data={withLeads} cx="50%" cy="50%" innerRadius={38} outerRadius={62} dataKey="leads" strokeWidth={0}>
                          {withLeads.map((_, i) => <Cell key={i} fill={CHART_PURPLE[i % CHART_PURPLE.length]} />)}
                        </Pie>
                        <Tooltip
                          contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                          formatter={(value) => [`${Number(value).toFixed(0)} leads`, '']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 space-y-1.5">
                      {withLeads.map((row, i) => (
                        <div key={row.meta_campaign_id} className="flex items-center justify-between gap-2 text-xs">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <div className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: CHART_PURPLE[i % CHART_PURPLE.length] }} />
                            <span className="truncate text-gray-600">{row.name || row.meta_campaign_id}</span>
                          </div>
                          <span className="whitespace-nowrap font-medium text-gray-900">
                            {row.leads.toLocaleString()} <span className="font-normal text-gray-400">({totalLeads > 0 ? ((row.leads / totalLeads) * 100).toFixed(0) : 0}%)</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          }

          case 'meta-creative-leaderboard': {
            const rows = [...(creativeResp?.rows ?? [])]
              .sort((a, b) => b.leads - a.leads || parseFloat(b.ctr ?? '0') - parseFloat(a.ctr ?? '0'))
              .slice(0, 5);
            return (
              <Card className="p-4 border border-gray-200 shadow-sm">
                <MetaWidgetHeading title="Top Ads" />
                {rows.length === 0 ? (
                  <p className="py-2 text-sm text-gray-500">{EMPTY_LINE}</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {rows.map((row) => (
                      // The creative leads the row (09-09): on Meta the image
                      // IS the ad, so a leaderboard of names is unscannable.
                      <div key={row.meta_ad_id} className="flex items-center gap-2.5 py-2">
                        <CreativeThumb
                          assetId={row.creative_asset_id}
                          alt={row.name || row.headline || row.meta_ad_id}
                          className="h-8 w-8"
                        />
                        <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-900">{row.name || row.headline || row.meta_ad_id}</span>
                        <span className="whitespace-nowrap text-xs text-gray-500">
                          {row.leads.toLocaleString()} leads · {parseFloat(row.ctr ?? '0').toFixed(2)}% CTR · {money(row.spend_cents)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          }

          case 'meta-placement-mix': {
            const rows = [...(placementResp?.rows ?? [])].sort((a, b) => b.spend_cents - a.spend_cents).slice(0, 6);
            const totalSpend = rows.reduce((s, r) => s + r.spend_cents, 0);
            return (
              <Card className="p-4 border border-gray-200 shadow-sm">
                <MetaWidgetHeading title="Placement Mix" />
                {rows.length === 0 ? (
                  <p className="py-2 text-sm text-gray-500">{EMPTY_LINE}</p>
                ) : (
                  <div className="space-y-2">
                    {rows.map((row, i) => (
                      <div key={row.value}>
                        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
                          <span className="min-w-0 truncate font-medium text-gray-900">{row.value}</span>
                          <span className="whitespace-nowrap text-gray-500">
                            {money(row.spend_cents)} · {row.leads.toLocaleString()} leads
                          </span>
                        </div>
                        <div className="h-1 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${totalSpend > 0 ? (row.spend_cents / totalSpend) * 100 : 0}%`,
                              background: CHART_PURPLE[i % CHART_PURPLE.length],
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          }

          default:
            return null;
        }
      }}
    </WidgetGrid>
  );
}
